import React, { useState, useEffect, useRef } from 'react';
import { useSession } from '@/hooks/useSession';
import ProfilePanel from './ProfilePanel';

export default function ProfileButton() {
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
  type PanelHandle = { clearIdle: () => void; startIdle: () => void; };
  const panelRef = useRef<PanelHandle | null>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const panelHeight = Math.min(window.innerHeight * 0.75, 520); // estimate
    if (rect.bottom + panelHeight > window.innerHeight) setPlacement('top'); else setPlacement('bottom');

    // If the button is currently hovered when the panel opens, clear the idle timer immediately
    try {
      if (buttonRef.current.matches(':hover')) {
        panelRef.current?.clearIdle();
      }
    } catch (e) {
      // matches may not be supported in some test environments
    }
  }, [open]);

  const initials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ').map((s: string) => s[0]).slice(0,2).join('')
    : (user?.email ? user.email.split('@')[0].slice(0,2).toUpperCase() : 'AN');

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        ref={buttonRef}
        aria-label="Open profile"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 text-secondary shadow-sm hover:scale-105 transition-transform ring-1 ring-primary/20"
        onClick={() => setOpen((s) => !s)}
        onMouseEnter={() => panelRef.current?.clearIdle()}
        onMouseLeave={() => panelRef.current?.startIdle()}
        onFocus={() => panelRef.current?.clearIdle()}
        onBlur={() => panelRef.current?.startIdle()}
      >
        {user?.user_metadata?.avatar_url ? (
          <img src={user.user_metadata.avatar_url} alt="avatar" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <span className="text-sm font-medium">{initials}</span>
        )}
      </button>

      {open && (
        <div className={`absolute right-0 z-50 ${placement === 'bottom' ? 'top-12' : 'bottom-12'}`}>
          <ProfilePanel ref={panelRef} placement={placement} onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
